import type { Plan } from "@roxpacer/core";

export interface MergeResult {
  /** Plans to keep locally, in display order. */
  plans: Plan[];
  /** Local plans that are newer than (or missing from) the account. */
  toUpload: Plan[];
}

const time = (plan: Plan) => Date.parse(plan.updatedAt);

/** A plan that was created but never edited, such as the starter plan. */
export function isUntouched(plan: Plan): boolean {
  return plan.createdAt === plan.updatedAt;
}

/**
 * Merges this browser's plans with the account's, keeping the newer copy of each.
 * Local plans missing from the account are uploaded; this is how anonymous plans
 * move into the account at sign-in. On that first merge (`atSignIn`), untouched
 * plans are skipped when the account already has plans, so the starter plan doesn't
 * pile up from every device. Later merges keep them: they were created while signed
 * in and may simply not be uploaded yet.
 */
export function mergePlans(
  local: Plan[],
  remote: Plan[],
  options: { atSignIn: boolean } = { atSignIn: false },
): MergeResult {
  const remoteById = new Map(remote.map((p) => [p.id, p]));
  const localIds = new Set(local.map((p) => p.id));
  const plans: Plan[] = [];
  const toUpload: Plan[] = [];

  for (const plan of local) {
    const server = remoteById.get(plan.id);
    if (!server) {
      if (options.atSignIn && remote.length > 0 && isUntouched(plan)) continue;
      plans.push(plan);
      toUpload.push(plan);
    } else if (time(plan) > time(server)) {
      plans.push(plan);
      toUpload.push(plan);
    } else {
      plans.push(server);
    }
  }

  const remoteOnly = remote.filter((p) => !localIds.has(p.id)).sort((a, b) => time(b) - time(a));
  return { plans: [...plans, ...remoteOnly], toUpload };
}
